import { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import { useMutation } from 'react-query';

import { postOneStopService } from '@/api/public';
import BasicButton from '@/components/Common/Button/BasicButton';
import TextInputLabelUp from '@/components/Common/Input/TextInputLabelUp';
import ConfirmModal from '@/components/Common/Modal/ConfirmModal';
import MenuHeader from '@/components/Header/MenuHeader';
import {
  CheckOutlineIcon,
  HeadphoneColorIcon,
  PhoneIcon,
  VehicleInspectionPlusColorIcon,
  VehicleInspectionSellColorIcon,
} from '@/components/Icon';
import { IS_ALREADY_APPLY_ONE_STOP_SERVICE } from '@/const/additionalService';
import { colors } from '@/const/colors';
import { REPRESENTATIVE_NUMBER } from '@/const/common';
import {
  COMMON_TOAST_ERROR,
  NAME_VALIDATION_LENGTH_MESSAGE,
  NAME_VALIDATION_MESSAGE,
  PHONE_NUMBER_VALIDATION_LENGTH_MESSAGE,
} from '@/const/errorMessage';
import { useToastContext } from '@/contexts/Common/ToastContext';
import useAuthRedirect from '@/hooks/useAuthRedirect';
import { memberAtom, updateAdditionalServiceAtom } from '@/store/member';
import { phoneCall } from '@/utils/common';
import { Validation, validateName, validatePhoneNumber } from '@/utils/validation';
import { Fab } from '@mui/material';

export interface OneStopServiceRequestData {
  name?: string;
  phoneNumber?: string;
}

const OneStopServiceRequest = () => {
  const [memberAtomData] = useAtom(memberAtom);
  const [, updateAdditionalService] = useAtom(updateAdditionalServiceAtom);

  const [isNearBottom, setIsNearBottom] = useState(false);
  const threshold = 20;

  const { showToast } = useToastContext();

  const authRedirect = useAuthRedirect();

  const [isDisabledRequestButton, setIsDisabledRequestButton] = useState(true);
  const [isAlreadyApply, setIsAlreadyApply] = useState(false);
  const [isPopupShow, setIsPopupShow] = useState(false);

  const [nameError, setNameError] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');

  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [phoneNumberErrorMessage, setPhoneNumberErrorMessage] = useState('');

  const [request, setRequest] = useState<OneStopServiceRequestData>({
    name: '',
    phoneNumber: '',
  });

  useEffect(() => {
    if (memberAtomData) {
      validateNameAndPhoneNumber(memberAtomData?.name, memberAtomData?.phoneNumber);
      setRequest({
        name: memberAtomData?.name,
        phoneNumber: memberAtomData?.phoneNumber,
      });
      setIsAlreadyApply(memberAtomData.isAlreadyApplyOneStopService);
    }
  }, [memberAtomData]);

  const validateNameAndPhoneNumber = (name?: string, phoneNumber?: string) => {
    if (name) {
      if (name.length > 20) {
        setNameErrorMessage(NAME_VALIDATION_LENGTH_MESSAGE);
        setNameError(true);
      } else if (validateName(name) === false) {
        setNameErrorMessage(NAME_VALIDATION_MESSAGE);
        setNameError(true);
      } else {
        setNameErrorMessage('');
        setNameError(false);
      }
    }

    if (phoneNumber) {
      if (phoneNumber.length > 11) {
        setPhoneNumberErrorMessage(PHONE_NUMBER_VALIDATION_LENGTH_MESSAGE);
        setPhoneNumberError(true);
      } else {
        const phoneNumberValidation = validatePhoneNumber(phoneNumber) as Validation;
        if (phoneNumberValidation.isValid === false) {
          setPhoneNumberErrorMessage(phoneNumberValidation.errorMessage);
          setPhoneNumberError(true);
        } else {
          setPhoneNumberErrorMessage('');
          setPhoneNumberError(false);
        }
      }
    }
  };

  const onChangeRequestInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'name') {
      validateNameAndPhoneNumber(value, request?.phoneNumber);
      setRequest((prev) => ({ ...prev, name: value }));
    } else {
      validateNameAndPhoneNumber(request.name, value);
      setRequest((prev) => ({ ...prev, phoneNumber: value }));
    }
  }, []);

  const { mutate, isLoading, isError, isSuccess } = useMutation(postOneStopService, {
    onSuccess: () => {
      showToast('알선판매 서비스를 신청했어요.', 'success', 'bottom');
      setIsAlreadyApply(true);
      updateAdditionalService(IS_ALREADY_APPLY_ONE_STOP_SERVICE);
    },
    onError: (error: AxiosError) => {
      if (error.code && error.message) {
        showToast(error.message, 'error', 'bottom');
      } else {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      }
    },
  });

  const onClickRequestOneStopService = () => {
    if (!authRedirect('/one-stop-service', { feature: '원스톱 서비스 신청' })) {
      return;
    }
    setIsPopupShow(true);
  };

  const handleClickApply = () => {
    mutate(request);
    setIsPopupShow(false);
  };

  useEffect(() => {
    setIsDisabledRequestButton(nameError || phoneNumberError || isLoading || isError || isSuccess);
  }, [nameError, phoneNumberError, isLoading, isError, isSuccess]);

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const viewportHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    setIsNearBottom(scrollTop + viewportHeight >= documentHeight - threshold);
  };

  const handleScrollBottom = () => {
    const currentScroll = window.scrollY;
    const viewportHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    if (currentScroll + viewportHeight < documentHeight) {
      window.scrollBy({
        top: viewportHeight,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    handleScroll();
  }, []);

  return (
    <>
      <MenuHeader title="알선판매 서비스"></MenuHeader>

      <div className="w-full pt-[60px] pb-[100px]">
        <div className="flex flex-col items-center">
          <p className="text-gray-8 text-[24px] font-bold text-center pt-6 pb-9">
            <span className="sm280:hidden">직거래 </span>알선판매 서비스란?
          </p>
          <div className="text-center">
            차량 판매 등록, 구매자 상담,
            <br className="hidden sm280:block" /> 거래 완료 시점까지
            <br className="sm280:hidden sm360:block sm680:hidden" />
            모든 판매 과정을
            <br className="hidden sm280:block" /> 한번에 도와드리는 서비스입니다.
          </div>

          <div className="flex flex-wrap justify-center gap-[12px] sm680:gap-[40px] py-[34px]">
            <div className="flex flex-col justify-center items-center bg-gray-1 w-[90px] h-[90px] rounded-[25px]">
              <VehicleInspectionPlusColorIcon width={37} height={37} />
              <div className="text-center font-semibold text-gray-8 text-[14px] leading-[16px] mt-[6px]">
                차량
                <br />
                판매 등록
              </div>
            </div>
            <div className="flex flex-col justify-center items-center bg-gray-1 w-[90px] h-[90px] rounded-[25px]">
              <HeadphoneColorIcon width={36} height={36} />
              <div className="text-center font-semibold text-gray-8 text-[14px] leading-[16px] mt-[6px]">
                구매자
                <br />
                고객 상담
              </div>
            </div>
            <div className="flex flex-col justify-center items-center bg-gray-1 w-[90px] h-[90px] rounded-[25px]">
              <VehicleInspectionSellColorIcon width={36} height={36} />
              <div className="text-center font-semibold text-gray-8 text-[14px] leading-[16px] mt-[6px]">
                차량
                <br />
                판매 완료
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-1 py-[46px] px-4 flex flex-col justify-center items-center">
          <h4 className="text-gray-8 font-bold text-[24px] mb-[24px]">이런 분에게 추천해요</h4>
          <ul className="flex flex-col gap-[14px] text-[15px] font-medium break-keep ">
            <li className="flex gap-1">
              <CheckOutlineIcon color={colors.primary[10]} />
              어플 사용에 어려움을 느끼시는 분
            </li>
            <li className="flex gap-1">
              <CheckOutlineIcon color={colors.primary[10]} />
              시간 제약으로 구매자 응대가 어려우신 분
            </li>
            <li className="flex gap-1">
              <CheckOutlineIcon color={colors.primary[10]} />
              <span className="text-pretty">거래 절차가 번거롭게 느껴지시는 분</span>
            </li>
          </ul>
        </div>

        <div className="px-4 pt-6">
          <div className="flex flex-col gap-[30px]">
            <TextInputLabelUp
              name="name"
              label="신청자명"
              placeholder="신청자명을 입력해주세요."
              value={request.name || ''}
              onChange={onChangeRequestInput}
              error={nameError}
              errorMsg={nameErrorMessage}
            />

            <TextInputLabelUp
              name="phoneNumber"
              label="휴대폰 번호"
              placeholder="휴대폰 번호를 입력해주세요."
              value={request.phoneNumber || ''}
              onChange={onChangeRequestInput}
              error={phoneNumberError}
              errorMsg={phoneNumberErrorMessage}
            />
          </div>
        </div>

        <div className="flex items-center justify-center fixed w-full mx-auto my-0 px-4 pb-2 bottom-0 left-0 right-0 h-14 max-w-[720px] min-w-[280px] bg-white">
          <div className="absolute right-[16px] top-[-72px]">
            <Fab
              sx={{
                backgroundColor: colors.gray[0],
                width: 52,
                height: 52,
                borderRadius: '50px',
                '&:hover': {
                  backgroundColor: colors.gray[0],
                },
                zIndex: 100,
              }}
              onClick={() => phoneCall(REPRESENTATIVE_NUMBER)}
            >
              <PhoneIcon color={colors.primary[10]}></PhoneIcon>
            </Fab>
          </div>

          <div className="absolute top-[-16px] left-0 w-full h-[16px] bg-gradient-to-b from-transparent to-[#ffffff]"></div>
          <BasicButton
            name={isAlreadyApply ? '서비스 신청완료' : isNearBottom ? '서비스 신청하기' : '아래로 내리기'}
            bgColor={colors.primary[10]}
            borderColor={colors.primary[10]}
            textColor={colors.gray[0]}
            fontSize={16}
            height={48}
            fontWeight={'bold'}
            isDisabled={isAlreadyApply || (isDisabledRequestButton && isNearBottom)}
            onClick={isNearBottom ? onClickRequestOneStopService : handleScrollBottom}
          />
        </div>
      </div>

      <ConfirmModal
        isOpen={isPopupShow}
        content={
          <span>
            알선판매 서비스를
            <br />
            신청하시겠어요?
          </span>
        }
        rightButtonLabel="신청하기"
        onLeftButtonClick={() => setIsPopupShow(false)}
        onRightButtonClick={handleClickApply}
      />
    </>
  );
};

export default OneStopServiceRequest;
